// src/pages/Admin/UserAdmin/UserDirectoryDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { FaDatabase, FaExternalLinkAlt, FaIdBadge, FaUsers } from 'react-icons/fa';
import { getUserDirectoryDetail, UserDirectoryDetail } from '@/api/userAdminApi';
import { usePageTitle } from '@/hooks/usePageTitle';
import './SpAccountPurgePage.scss';

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '-');

function auditVariant(type?: string) {
  if (type === 'I') return 'success';
  if (type === 'U') return 'primary';
  if (type === 'D') return 'danger';
  return 'secondary';
}

function auditLabel(type?: string) {
  if (type === 'I') return 'Inserted';
  if (type === 'U') return 'Updated';
  if (type === 'D') return 'Deleted';
  return type || '-';
}

function Value({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="spa-detail-value">
      <div className="spa-detail-label">{label}</div>
      <div>{value || '-'}</div>
    </div>
  );
}

const UserDirectoryDetailPage: React.FC = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const [data, setData] = useState<UserDirectoryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!identifier) return;
      setLoading(true);
      setError(null);
      try {
        const result = await getUserDirectoryDetail(identifier);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setError('Failed to load user details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [identifier]);

  const entra = data?.entraUser;
  const employee = data?.employeeHierarchyUser;
  const title = entra?.displayName || [employee?.employeeFirstName, employee?.employeeLastName].filter(Boolean).join(' ') || identifier || 'User';
  usePageTitle(data ? title : null, 'User Details');

  return (
    <Container className="mt-4 spa-page">
      <div className="mb-4">
        <h2 className="mb-1"><FaUsers className="me-2 text-primary" />{title}</h2>
        <p className="text-muted mb-0">User directory profile and resource access snapshot.</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          {entra?.effEndDt && (
            <Alert variant="secondary">
              This Entra record was removed from the active dataset on {fmt(entra.effEndDt)}. Permanent deletion status is not checked by this page yet.
            </Alert>
          )}

          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold"><FaDatabase className="me-2" />Entra User</Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col sm={6}><Value label="Display Name" value={entra?.displayName} /></Col>
                    <Col sm={6}><Value label="Status" value={<Badge bg={entra?.effEndDt ? 'secondary' : 'success'}>{entra?.effEndDt ? 'Removed' : 'Active'}</Badge>} /></Col>
                    <Col sm={6}><Value label="Email" value={entra?.email} /></Col>
                    <Col sm={6}><Value label="UPN" value={entra?.upn} /></Col>
                    <Col sm={6}><Value label="Employee #" value={entra?.employeeNum} /></Col>
                    <Col sm={6}><Value label="NTID" value={entra?.employeeWindowsNtid} /></Col>
                    <Col sm={6}><Value label="Department" value={entra?.employeeDeptId} /></Col>
                    <Col sm={6}><Value label="Manager" value={entra?.managerEmpNum || entra?.managerWindowsNtid} /></Col>
                    <Col sm={12}><Value label="Org Hierarchy" value={entra?.orgHierarchy} /></Col>
                    <Col sm={12}><Value label="Org Hierarchy Dept" value={entra?.orgHierarchyDept} /></Col>
                    <Col sm={6}><Value label="Last Crawl" value={fmt(entra?.lastCrawlDt)} /></Col>
                    <Col sm={6}><Value label="Removed" value={fmt(entra?.effEndDt)} /></Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold"><FaIdBadge className="me-2" />Employee Hierarchy</Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col sm={6}><Value label="Name" value={[employee?.employeeFirstName, employee?.employeeLastName].filter(Boolean).join(' ')} /></Col>
                    <Col sm={6}><Value label="Employee Type" value={employee?.employeeType ? <Badge bg="info">{employee.employeeType}</Badge> : undefined} /></Col>
                    <Col sm={6}><Value label="Email" value={employee?.employeeEmail} /></Col>
                    <Col sm={6}><Value label="Employee #" value={employee?.employeeNum} /></Col>
                    <Col sm={6}><Value label="NTID" value={employee?.employeeWindowsNtid} /></Col>
                    <Col sm={6}><Value label="Department" value={employee?.employeeDeptId} /></Col>
                    <Col sm={12}><Value label="Position" value={employee?.employeePositionTitle} /></Col>
                    <Col sm={6}><Value label="Start Date" value={fmtDate(employee?.employeeStartDate)} /></Col>
                    <Col sm={6}><Value label="End Date" value={fmtDate(employee?.employeeEndDate)} /></Col>
                    <Col sm={6}><Value label="Manager" value={[
                      [employee?.managerFirstName, employee?.managerLastName].filter(Boolean).join(' '),
                      employee?.managerEmail,
                      employee?.managerPositionTitle,
                    ].filter(Boolean).join(' | ') || employee?.managerEmpId || employee?.managerWindowsNtid} /></Col>
                    <Col sm={6}><Value label="Loaded" value={fmt(employee?.loadDate)} /></Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {data?.departmentAuditHistory && data.departmentAuditHistory.length > 0 && (
            <Card className="mb-4">
              <Card.Header className="fw-semibold">Department Hierarchy Audit</Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover size="sm" className="spa-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Change</th>
                        <th>Employee Department</th>
                        <th>Org Structure</th>
                        <th>Org Departments</th>
                        <th>Effective</th>
                        <th>Updated</th>
                        <th>Loaded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.departmentAuditHistory.map((row) => (
                        <tr key={row.auditEmployeeHierarchyDepartmentId}>
                          <td><Badge bg={auditVariant(row.type)}>{auditLabel(row.type)}</Badge></td>
                          <td>{row.employeeDepartment || '-'}</td>
                          <td className="small" style={{ minWidth: 220 }}>{row.orgStructure || '-'}</td>
                          <td className="small" style={{ minWidth: 220 }}>{row.orgStructureDepartments || '-'}</td>
                          <td className="text-nowrap small">
                            <div>Start: {fmt(row.effStartDt)}</div>
                            <div className="text-muted">End: {fmt(row.effEndDt)}</div>
                          </td>
                          <td className="text-nowrap text-muted">{fmt(row.lastUpdateDt || row.createDt)}</td>
                          <td className="text-nowrap text-muted">{fmtDate(row.loadDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header className="fw-semibold">Resource Access Snapshot</Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover size="sm" className="spa-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Source</th>
                      <th>Primary Owner</th>
                      <th>Secondary Owner</th>
                      <th>Last Crawl</th>
                      <th>Last Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.resources.map((r) => (
                      <tr key={`${r.internalResourceId}-${r.userRole}-${r.relationshipSource}`}>
                        <td>
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noreferrer">
                              {r.displayName || r.url}<FaExternalLinkAlt className="ms-2 small" />
                            </a>
                          ) : r.displayName || '-'}
                          {r.groupId && <div className="text-muted small">{r.groupId}</div>}
                        </td>
                        <td>{r.resourceType || '-'}</td>
                        <td><Badge bg={r.userRole === 'Owner' ? 'primary' : 'secondary'}>{r.userRole || '-'}</Badge></td>
                        <td>{r.relationshipSource || '-'}</td>
                        <td>{r.primaryOwner || '-'}</td>
                        <td>{r.secondaryOwner || '-'}</td>
                        <td className="text-nowrap text-muted">{fmt(r.lastCrawlDt)}</td>
                        <td className="text-nowrap text-muted">{fmt(r.lastUserActionDt)}</td>
                      </tr>
                    ))}
                    {data?.resources.length === 0 && (
                      <tr><td colSpan={8} className="text-center text-muted py-4">No resource ownership or membership snapshot found</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default UserDirectoryDetailPage;
